import {
  Box,
  CardContent,
  CardMedia,
  Paper,
  styled,
  Typography,
  Button,
} from "@mui/material";
import { lazy, Suspense } from "react";
import { Product } from "utils/types/ProductTypes";
import { useNavigate } from "react-router-dom";
import { useUtility } from "utils/context/UtilityContext";
import { SetFlag, useWindowSize } from "utils";

const PriceButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  backgroundColor: "#254c78",
  color: "white",
  borderRadius: 12,
  padding: theme.spacing(1.25, 2),
  textTransform: "none",
  fontWeight: 700,
  "&:hover": {
    backgroundColor: "#203b5b",
  },
}));

const BestsellerTag = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: "#f5d000",
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  fontSize: "0.75rem",
  fontWeight: "bold",
  zIndex: 1,
}));

const BSBCoin = lazy(() => import("../BSBCoin"));

interface ProductBestsellerCardProps {
  product: Product;
}

export const ProductBestsellerCard: React.FC<ProductBestsellerCardProps> = ({
  product,
}) => {
  const { isPC } = useWindowSize();
  const navigate = useNavigate();
  const { addToCart } = useUtility();

  const handleNavigate = () => {
    navigate(`/provider/${product.provider}`);
  };

  const handleBuyNow = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart(product);
    navigate(`/cart-summary`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        px: { xs: 1, sm: 2 },
        py: 1,
      }}
    >
      <Paper
        elevation={4}
        onClick={handleNavigate}
        sx={{
          width: isPC ? "70%" : "100%", // <-- plus large sur desktop
          minWidth: 320,
          maxWidth: 1280, // <-- plafonné plus haut
          display: "flex",
          flexDirection: isPC ? "row" : "column",
          position: "relative",
          backgroundColor: "#e8edf2",
          cursor: "pointer",
          borderRadius: 14,
          overflow: "hidden",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: (theme) => theme.shadows[6],
          },
        }}
        aria-label={`Voir ${product.name}`}
        role="button"
      >
        <BestsellerTag>Bestseller</BestsellerTag>

        <CardMedia
          component="img"
          image={product.image}
          alt={product.name}
          sx={{
            width: isPC ? "50%" : "100%",
            height: isPC ? 300 : 220,
            objectFit: "contain",
            bgcolor: "#f7f9fc",
            p: 2,
            borderRight: isPC ? "1px solid #dde3ea" : "none",
            borderBottom: isPC ? "none" : "1px solid #dde3ea",
          }}
          loading="lazy"
        />

        <CardContent
          sx={{
            width: isPC ? "50%" : "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.25,
            p: { xs: 2, sm: 3 },
            textAlign: "center",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            justifyContent="center"
          >
            <Typography
              variant="h6"
              component="div"
              fontWeight={800}
              lineHeight={1.2}
            >
              {product.name}
            </Typography>
            <SetFlag
              countryCode={product.countryCode}
              countryName={product.country}
            />
          </Box>

          <Typography variant="body1" color="text.secondary">
            {product.price} {product.currency}
          </Typography>

          <PriceButton
            variant="contained"
            onClick={handleBuyNow}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
            aria-label={`Acheter ${product.name}`}
          >
            <Suspense fallback={null}>
              <BSBCoin />
            </Suspense>
            {product.priceUSD}
          </PriceButton>
        </CardContent>
      </Paper>
    </Box>
  );
};
